import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { orpc } from "@/client";
import { MessageCache } from "@/lib/message-cache";
import { Button } from "@/components/ui/button";
import { MessageContent } from "./message-content";
import type { Message } from "@/api/db/messages";
import { djs } from "@/lib/djs";
import { cn } from "@/lib/utils";

const DELIVERY_LABEL = {
	received: null,
	queued: "Na fila",
	sending: "Enviando",
	accepted: "Envio aceito",
	sent: "Enviada",
	delivered: "Entregue",
	read: "Lida",
	failed: "Falha no envio",
	unknown: "Envio sem confirmação",
	simulated: "Simulação",
};

export function MessageBubble({ message }: { message: Message }) {
	const operation = useRef(crypto.randomUUID());
	const client = useQueryClient();
	const retry = useMutation({
		mutationFn: () =>
			orpc.send.retry.call({ id: message.send_id!, operation_id: operation.current }),
		onSuccess: async (result) => {
			if (!result.queued) {
				toast.error("O estado do envio mudou. Confira a conversa antes de tentar novamente.");
			}
			await MessageCache.for(client).all();
		},
		onError: () =>
			toast.error(
				"Não foi possível confirmar a nova tentativa. Tentar de novo consulta o mesmo envio.",
			),
	});
	const retryLabel = retry.isSuccess ? "Nova tentativa registrada" : "Tentar novamente";
	const fromClinic = message.direction === "clinic";

	const author = message.author_name ?? message.operator_name;

	const sealText = message.delivery ? DELIVERY_LABEL[message.delivery] : null;

	return (
		<li
			data-component="message-bubble"
			data-message-id={message.id}
			data-direction={message.direction}
			data-local={message.local ?? ""}
			className={cn("flex flex-col gap-1", fromClinic ? "items-end" : "items-start")}
		>
			<div
				className={cn(
					"max-w-[85%] rounded-lg px-3.5 py-2 text-sm leading-relaxed text-foreground",
					fromClinic
						? "rounded-br-sm bg-primary-soft"
						: "rounded-bl-sm border border-border bg-card",
				)}
			>
				<MessageContent message={message} />
				{!!message.template_buttons?.length && (
					<div className="mt-2 flex flex-col border-t border-primary/15 text-center text-primary">
						{message.template_buttons.map((label, index) => (
							<span
								key={`${index}:${label}`}
								className="border-b border-primary/15 py-2 last:border-b-0"
							>
								{label}
							</span>
						))}
					</div>
				)}
			</div>

			<div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-muted-foreground">
				<span className="font-mono">{djs(message.sent_at).format("DD/MM/YY HH:mm")}</span>

				{fromClinic && !!author && <span className="font-medium">{author}</span>}

				{!!sealText && (
					<span
						data-slot="bubble-seal"
						className={cn(
							"font-medium",
							message.delivery === "failed" ? "text-destructive" : "text-primary",
						)}
					>
						{sealText}
					</span>
				)}
				{message.delivery === "failed" && message.send_id !== null && (
					<Button
						variant="ghost"
						size="sm"
						disabled={retry.isPending || retry.isSuccess}
						onClick={() => retry.mutate()}
					>
						{retryLabel}
					</Button>
				)}
			</div>
		</li>
	);
}
